import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, Calendar, MapPin, Users, LogOut } from 'lucide-react';

const API = '/api/volunteer-portal';

interface VolunteerInfo {
  _id: string; name: string; phone?: string; email?: string;
  skills?: string[]; area?: string; availability?: string;
  totalHours: number; hoursLog?: any[];
}

interface Shift {
  _id: string; title: string; date: string; startTime: string; endTime: string;
  location?: string; projectName?: string; maxVolunteers?: number;
  assignments: any[]; status: string;
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
const LoginScreen: React.FC<{ orgId: string; onLogin?: (token: string, vol: any) => void }> = ({ orgId }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await axios.post(`${API}/send-magic-link`, { email, orgId });
      setSent(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'שגיאה בשליחת הקישור');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D47A1] via-[#1565C0] to-[#1976D2] flex items-center justify-center p-4" dir="rtl">
      <div className="bg-white rounded-3xl shadow-2xl p-8 w-full max-w-sm">
        <div className="text-center mb-6">
          <div className="text-4xl mb-2">🤝</div>
          <h1 className="text-xl font-bold text-gray-800">פורטל מתנדבים</h1>
          <p className="text-gray-500 text-sm mt-1">הזן את המייל שלך לקבלת קישור כניסה</p>
        </div>
        {sent ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">📬</div>
            <p className="font-semibold text-gray-800">הקישור נשלח!</p>
            <p className="text-gray-500 text-sm mt-1">בדוק את תיבת הדואר שלך ולחץ על הקישור לכניסה.</p>
            <p className="text-gray-400 text-xs mt-2">תקף ל-2 שעות</p>
          </div>
        ) : (
          <form onSubmit={handleSend} className="space-y-4">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus
              placeholder="your@email.com" dir="ltr"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-[#1565C0] outline-none" />
            {error && <p className="text-red-500 text-sm text-center">⚠️ {error}</p>}
            <button type="submit" disabled={loading}
              className="w-full bg-[#1565C0] hover:bg-[#0D47A1] text-white font-bold py-3 rounded-xl disabled:opacity-50">
              {loading ? 'שולח...' : 'שלח קישור כניסה →'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ─── Portal App ───────────────────────────────────────────────────────────────
const PortalApp: React.FC<{ token: string; vol: VolunteerInfo; onLogout: () => void }> = ({ token, vol: initialVol, onLogout }) => {
  const [tab, setTab] = useState<'shifts' | 'my-shifts' | 'profile'>('shifts');
  const [vol] = useState(initialVol);
  const [openShifts, setOpenShifts] = useState<Shift[]>([]);
  const [myShifts, setMyShifts] = useState<Shift[]>([]);
  const [totalHours, setTotalHours] = useState(vol.totalHours || 0);
  const [loading, setLoading] = useState(false);
  const [signupMsg, setSignupMsg] = useState<{ id: string; msg: string; ok: boolean } | null>(null);

  const headers = { 'x-portal-token': token };

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const [openRes, myRes] = await Promise.all([
        axios.get(`${API}/shifts`, { headers }),
        axios.get(`${API}/my-shifts`, { headers }),
      ]);
      setOpenShifts(openRes.data || []);
      setMyShifts(myRes.data.shifts || []);
      setTotalHours(myRes.data.totalHours || 0);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchShifts(); }, [fetchShifts]);

  const handleSignup = async (shiftId: string) => {
    try {
      const res = await axios.post(`${API}/shifts/${shiftId}/signup`, {}, { headers });
      setSignupMsg({ id: shiftId, msg: res.data.message, ok: true });
      fetchShifts();
      setTimeout(() => setSignupMsg(null), 3000);
    } catch (err: any) {
      setSignupMsg({ id: shiftId, msg: err?.response?.data?.error || 'שגיאה', ok: false });
      setTimeout(() => setSignupMsg(null), 3000);
    }
  };

  const handleCancel = async (shiftId: string) => {
    if (!confirm('לבטל הרשמה למשמרת זו?')) return;
    await axios.delete(`${API}/shifts/${shiftId}/signup`, { headers });
    fetchShifts();
  };

  const handleConfirmAttendance = async (shiftId: string) => {
    try {
      await axios.post(`${API}/shifts/${shiftId}/confirm`, {}, { headers });
      fetchShifts();
    } catch (err: any) {
      alert(err?.response?.data?.error || 'שגיאה');
    }
  };

  const isSignedUp = (shift: Shift) => shift.assignments.some((a: any) => a.volunteerId === vol._id);
  const spotsLeft = (shift: Shift) => (shift.maxVolunteers || 99) - shift.assignments.length;

  const formatDate = (d: string) => new Date(d).toLocaleDateString('he-IL', { weekday: 'short', day: 'numeric', month: 'long' });

  return (
    <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#121212]" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-l from-[#0D47A1] to-[#1976D2] text-white px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <p className="font-bold text-lg">שלום {vol.name} 👋</p>
            <p className="text-white/70 text-xs">{totalHours} שעות · מתנדב פעיל</p>
          </div>
          <button onClick={onLogout} className="p-2 bg-white/10 rounded-xl hover:bg-white/20 transition-all">
            <LogOut size={18} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-lg mx-auto px-4 pt-4">
        <div className="flex gap-1 bg-white dark:bg-[#1E1E1E] rounded-xl p-1 shadow-sm mb-4">
          {([['shifts','📅 משמרות פתוחות'],['my-shifts','⏱ המשמרות שלי'],['profile','👤 הפרופיל שלי']] as const).map(([t,l]) => (
            <button key={t} onClick={() => setTab(t)}
              className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-all ${tab===t?'bg-[#1565C0] text-white shadow-sm':'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>

        {/* Open Shifts Tab */}
        {tab === 'shifts' && (
          <div className="space-y-3 pb-8">
            {loading ? <p className="text-center py-8 text-gray-400">טוען...</p> :
            openShifts.length === 0 ? (
              <div className="text-center py-16 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">🗓️</p>
                <p className="text-gray-500">אין משמרות פתוחות כרגע</p>
              </div>
            ) : openShifts.map(shift => {
              const signed = isSignedUp(shift);
              const spots = spotsLeft(shift);
              return (
                <div key={shift._id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-white">{shift.title}</p>
                      {shift.projectName && <p className="text-xs text-gray-400 mt-0.5">📁 {shift.projectName}</p>}
                    </div>
                    {signed ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">✅ רשום</span>
                    ) : spots <= 0 ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full font-medium">🕐 המתנה</span>
                    ) : (
                      <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full font-medium">{spots} מקומות</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><Calendar size={12} />{formatDate(shift.date)}</span>
                    <span className="flex items-center gap-1"><Clock size={12} />{shift.startTime}–{shift.endTime}</span>
                    {shift.location && <span className="flex items-center gap-1"><MapPin size={12} />{shift.location}</span>}
                    <span className="flex items-center gap-1"><Users size={12} />{shift.assignments.length}/{shift.maxVolunteers}</span>
                  </div>
                  {signupMsg?.id === shift._id && (
                    <p className={`text-xs mb-2 font-medium ${signupMsg.ok ? 'text-green-600' : 'text-red-500'}`}>{signupMsg.msg}</p>
                  )}
                  {signed ? (
                    <button onClick={() => handleCancel(shift._id)}
                      className="w-full py-2.5 border-2 border-red-200 text-red-500 font-semibold text-sm rounded-xl hover:bg-red-50 transition-all">
                      ביטול הרשמה
                    </button>
                  ) : (
                    <button onClick={() => handleSignup(shift._id)}
                      className="w-full py-2.5 bg-[#1565C0] hover:bg-[#0D47A1] text-white font-semibold text-sm rounded-xl transition-all">
                      {spots <= 0 ? 'הצטרף לרשימת המתנה' : 'הירשם למשמרת'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* My Shifts Tab */}
        {tab === 'my-shifts' && (
          <div className="space-y-3 pb-8">
            <div className="bg-gradient-to-l from-[#1565C0] to-[#6200EA] text-white rounded-2xl p-4 flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                <Clock size={28} />
              </div>
              <div>
                <p className="text-3xl font-extrabold">{totalHours}</p>
                <p className="text-white/80 text-sm">שעות התנדבות מצטברות</p>
              </div>
            </div>

            {myShifts.length === 0 ? (
              <div className="text-center py-12 bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm">
                <p className="text-4xl mb-2">📋</p>
                <p className="text-gray-500">עוד לא השתתפת במשמרות</p>
              </div>
            ) : myShifts.map(shift => (
              <div key={shift._id} className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-4 border border-[#E0E0E0] dark:border-[#2C2C2C]">
                <p className="font-semibold text-gray-800 dark:text-white">{shift.title}</p>
                <div className="flex gap-3 text-xs text-gray-500 mt-1">
                  <span>{formatDate(shift.date)}</span>
                  <span>{shift.startTime}–{shift.endTime}</span>
                </div>
                {(() => {
                  const a = shift.assignments.find((x: any) => x.volunteerId === vol._id);
                  const isFuture = new Date(shift.date) > new Date();
                  return a && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${a.status==='confirmed'?'bg-green-100 text-green-700':a.status==='declined'?'bg-red-100 text-red-600':'bg-amber-100 text-amber-700'}`}>
                        {a.status==='confirmed'?'✅ אישרתי הגעה':a.status==='declined'?'❌ לא אושר':'⏳ ממתין לאישור'}
                      </span>
                      {isFuture && a.status !== 'confirmed' && (
                        <button onClick={() => handleConfirmAttendance(shift._id)}
                          className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-full transition-all">
                          אשר הגעה ✅
                        </button>
                      )}
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="space-y-4 pb-8">
            <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl shadow-sm p-5 border border-[#E0E0E0] dark:border-[#2C2C2C]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1565C0] to-[#6200EA] flex items-center justify-center text-white text-xl font-bold">
                  {vol.name.charAt(0)}
                </div>
                <div>
                  <p className="font-bold text-lg text-gray-800 dark:text-white">{vol.name}</p>
                  <p className="text-gray-400 text-sm">{vol.email}</p>
                </div>
              </div>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                  <span className="text-gray-500">טלפון</span>
                  <span className="font-medium dir-ltr">{vol.phone || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                  <span className="text-gray-500">אזור</span>
                  <span className="font-medium">{vol.area || '—'}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-[#F0F0F0] dark:border-[#2C2C2C]">
                  <span className="text-gray-500">זמינות</span>
                  <span className="font-medium">{vol.availability || '—'}</span>
                </div>
                <div className="flex justify-between py-2">
                  <span className="text-gray-500">כישורים</span>
                  <span className="font-medium text-left">{vol.skills?.join(', ') || '—'}</span>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl px-4 py-3 text-sm text-blue-700 dark:text-blue-300">
              💡 לעדכון פרטים, פנה למנהל הארגון שלך
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const VolunteerPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const magicToken = searchParams.get('token');
  const orgIdParam = searchParams.get('org') || '';

  const [authToken, setAuthToken] = useState<string | null>(() => localStorage.getItem('volunteer_portal_token'));
  const [vol, setVol] = useState<VolunteerInfo | null>(() => {
    try { return JSON.parse(localStorage.getItem('volunteer_portal_user') || 'null'); } catch { return null; }
  });
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  // Auto-verify magic link token from URL
  useEffect(() => {
    if (magicToken && !authToken) {
      setVerifying(true);
      axios.post(`${API}/verify-token`, { token: magicToken })
        .then(res => {
          localStorage.setItem('volunteer_portal_token', res.data.token);
          localStorage.setItem('volunteer_portal_user', JSON.stringify(res.data.volunteer));
          setAuthToken(res.data.token);
          setVol(res.data.volunteer);
          window.history.replaceState({}, '', '/volunteer-portal');
        })
        .catch(() => setError('הקישור לא תקין או פג תוקפו'))
        .finally(() => setVerifying(false));
    }
  }, [magicToken]);

  // Load full volunteer info
  useEffect(() => {
    if (authToken && vol) {
      axios.get(`${API}/me`, { headers: { 'x-portal-token': authToken } })
        .then(res => { setVol(res.data); localStorage.setItem('volunteer_portal_user', JSON.stringify(res.data)); })
        .catch(() => { /* token expired */ handleLogout(); });
    }
  }, [authToken]);

  const handleLogin = (token: string, volunteer: any) => {
    localStorage.setItem('volunteer_portal_token', token);
    localStorage.setItem('volunteer_portal_user', JSON.stringify(volunteer));
    setAuthToken(token);
    setVol(volunteer);
  };

  const handleLogout = () => {
    localStorage.removeItem('volunteer_portal_token');
    localStorage.removeItem('volunteer_portal_user');
    setAuthToken(null);
    setVol(null);
  };

  if (verifying) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0D47A1] to-[#1976D2]">
      <div className="text-white text-center"><div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mx-auto mb-4" /><p>מאמת...</p></div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0D47A1] to-[#1976D2]" dir="rtl">
      <div className="bg-white rounded-3xl p-8 text-center max-w-sm">
        <p className="text-4xl mb-3">😕</p>
        <p className="font-bold text-gray-800 mb-2">{error}</p>
        <button onClick={() => setError('')} className="text-[#1565C0] text-sm hover:underline">נסה שוב</button>
      </div>
    </div>
  );

  if (!authToken || !vol) {
    return <LoginScreen orgId={orgIdParam} onLogin={handleLogin} />;
  }

  return <PortalApp token={authToken} vol={vol} onLogout={handleLogout} />;
};

export default VolunteerPortalPage;
